import { useState, useEffect, useCallback, useMemo } from 'react';
import { flujoCajaApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DatePickerInput } from '../ui/date-picker';
import { toast } from '../../hooks/use-toast';
import { AlertTriangle, CalendarClock, CheckCircle2, Download, Filter, Pencil, Plus, Search, Trash2, Wallet, Repeat } from 'lucide-react';

const formatCLP = (value) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value || 0);

const todayISO = () => new Date().toISOString().split('T')[0];

const emptyForm = () => ({ nombre: '', vence: todayISO(), monto: 0, activo: true, dia_mes: null });

export default function ProvisionesPanel() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filtro, setFiltro] = useState('activas'); // activas | inactivas | todas | proximas
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | item
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtro === 'activas' ? true : filtro === 'inactivas' ? false : undefined;
      const res = await flujoCajaApi.listProvisiones(params);
      setItems(res.data.items || []);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al cargar provisiones', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const today = todayISO();
    const in91 = new Date();
    in91.setDate(in91.getDate() + 91);
    const in91ISO = in91.toISOString().split('T')[0];

    return items.filter((p) => {
      if (search && !p.nombre.toLowerCase().includes(search.toLowerCase())) return false;
      if (filtro === 'proximas') {
        return p.activo && p.vence >= today && p.vence <= in91ISO;
      }
      return true;
    });
  }, [items, search, filtro]);

  const totalActivo = useMemo(() => items.filter(p => p.activo).reduce((a, p) => a + p.monto, 0), [items]);
  const totalVencidoActivo = useMemo(() => {
    const t = todayISO();
    return items.filter(p => p.activo && p.vence < t).reduce((a, p) => a + p.monto, 0);
  }, [items]);
  const totalProximos91 = useMemo(() => {
    const t = todayISO();
    const in91 = new Date();
    in91.setDate(in91.getDate() + 91);
    const in91ISO = in91.toISOString().split('T')[0];
    return items.filter(p => p.activo && p.vence >= t && p.vence <= in91ISO).reduce((a, p) => a + p.monto, 0);
  }, [items]);

  const openNew = () => { setForm(emptyForm()); setEditing('new'); };
  const openEdit = (p) => { setForm({ nombre: p.nombre, vence: p.vence, monto: p.monto, activo: p.activo, dia_mes: p.dia_mes || null }); setEditing(p); };
  const close = () => { setEditing(null); setForm(emptyForm()); };

  const save = async () => {
    if (!form.nombre.trim()) { toast({ title: 'Nombre requerido', variant: 'destructive' }); return; }
    if (!form.vence) { toast({ title: 'Fecha de vencimiento requerida', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await flujoCajaApi.createProvision(form);
        toast({ title: 'Provisión creada' });
      } else {
        await flujoCajaApi.updateProvision(editing.id, form);
        toast({ title: 'Provisión actualizada' });
      }
      close();
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (p) => {
    try {
      await flujoCajaApi.updateProvision(p.id, { ...p, activo: !p.activo });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const doDelete = async (p) => {
    setDeleting(p.id);
    try {
      await flujoCajaApi.deleteProvision(p.id);
      toast({ title: `"${p.nombre}" eliminada` });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  const generarMeses = async (p) => {
    const meses = parseInt(window.prompt(
      `Generar próximos N meses de "${p.nombre}".\n` +
      (p.dia_mes ? `Día del mes configurado: ${p.dia_mes}` : '⚠ Sin día del mes configurado — se usará el día de la fecha actual') +
      '\n\n¿Cuántos meses crear?', '12'
    ), 10);
    if (!meses || meses < 1 || meses > 60) return;
    try {
      const res = await flujoCajaApi.generarMesesProvision(p.id, meses);
      toast({ title: `${res.data.generadas} provisiones generadas (${res.data.solicitadas} solicitadas)` });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const today = todayISO();

  return (
    <Card data-testid="provisiones-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="w-4 h-4 text-amber-600" /> Provisiones (Fila 19 del Flujo)
            </CardTitle>
            <CardDescription>
              Gastos a provisionar con fecha de vencimiento. Las activas dentro de los próximos 91 días se suman automáticamente al grid.
            </CardDescription>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" onClick={openNew} data-testid="btn-new-provision">
              <Plus className="w-4 h-4 mr-1" /> Nueva
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
          <div className="flex items-center justify-between p-2.5 rounded-md border bg-amber-50 dark:bg-amber-950/20">
            <span className="text-xs font-medium text-amber-900 dark:text-amber-200 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Total activas
            </span>
            <span className="text-sm font-bold tabular-nums text-amber-900 dark:text-amber-200" data-testid="kpi-total-activas">
              {formatCLP(totalActivo)}
            </span>
          </div>
          <div className="flex items-center justify-between p-2.5 rounded-md border bg-red-50 dark:bg-red-950/20">
            <span className="text-xs font-medium text-red-900 dark:text-red-200 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Vencidas activas
            </span>
            <span className="text-sm font-bold tabular-nums text-red-700 dark:text-red-300" data-testid="kpi-vencidas">
              {formatCLP(totalVencidoActivo)}
            </span>
          </div>
          <div className="flex items-center justify-between p-2.5 rounded-md border bg-blue-50 dark:bg-blue-950/20">
            <span className="text-xs font-medium text-blue-900 dark:text-blue-200 flex items-center gap-1">
              <CalendarClock className="w-3.5 h-3.5" /> Próximos 91 días
            </span>
            <span className="text-sm font-bold tabular-nums text-blue-700 dark:text-blue-300" data-testid="kpi-proximas">
              {formatCLP(totalProximos91)}
            </span>
          </div>
        </div>

        {/* Gastos Diarios Imprevistos config */}
        <GastosDiariosCard onSaved={() => { /* el grid se invalida en el backend */ }} />

        {/* Filtros */}
        <div className="flex flex-wrap gap-2 mt-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 h-8 text-sm"
              data-testid="input-search-provisiones"
            />
          </div>
          <div className="flex items-center gap-1">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            {[
              { v: 'activas', l: 'Activas' },
              { v: 'proximas', l: 'Próximas 91d' },
              { v: 'inactivas', l: 'Inactivas' },
              { v: 'todas', l: 'Todas' },
            ].map((o) => (
              <Button
                key={o.v}
                size="sm"
                variant={filtro === o.v ? 'default' : 'outline'}
                className="text-xs h-7 px-2.5"
                onClick={() => setFiltro(o.v)}
                data-testid={`filter-prov-${o.v}`}
              >
                {o.l}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <Skeleton className="h-40" />
        ) : (
          <div className="rounded-md border overflow-hidden max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="table-provisiones">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-[36%]">Nombre</th>
                  <th className="text-left px-3 py-2 font-medium w-[120px]">Vence</th>
                  <th className="text-center px-3 py-2 font-medium w-[70px]" title="Día típico del mes para recurrencia">Día mes</th>
                  <th className="text-right px-3 py-2 font-medium w-[140px]">Monto</th>
                  <th className="text-center px-3 py-2 font-medium w-[70px]">Activo</th>
                  <th className="text-right px-3 py-2 font-medium w-[120px]">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const vencido = p.vence < today;
                  return (
                    <tr key={p.id} className={`border-t hover:bg-muted/30 ${!p.activo ? 'opacity-50' : ''}`} data-testid={`row-prov-${p.id}`}>
                      <td className="px-3 py-1.5">{p.nombre}</td>
                      <td className="px-3 py-1.5 tabular-nums">
                        <span className={vencido && p.activo ? 'text-red-600 font-semibold' : ''}>{p.vence}</span>
                        {vencido && p.activo && <Badge variant="destructive" className="ml-1 text-[10px] px-1 py-0">Vencido</Badge>}
                      </td>
                      <td className="px-3 py-1.5 text-center tabular-nums text-xs">
                        {p.dia_mes ? (
                          <Badge variant="outline" className="px-2 py-0">{p.dia_mes}</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{formatCLP(p.monto)}</td>
                      <td className="px-3 py-1.5 text-center">
                        <Switch
                          checked={p.activo}
                          onCheckedChange={() => toggleActivo(p)}
                          data-testid={`switch-activo-${p.id}`}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Generar próximos meses" onClick={() => generarMeses(p)} data-testid={`btn-gen-${p.id}`}>
                          <Repeat className="w-3.5 h-3.5 text-blue-600" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`btn-edit-${p.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-red-600 hover:text-red-700"
                          disabled={deleting === p.id}
                          onClick={() => { if (window.confirm(`¿Eliminar "${p.nombre}"?`)) doDelete(p); }}
                          data-testid={`btn-delete-${p.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">
                    No hay provisiones {filtro === 'activas' ? 'activas' : filtro === 'inactivas' ? 'inactivas' : filtro === 'proximas' ? 'próximas' : ''}.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Dialog create/edit */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md" data-testid="dialog-provision">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nueva Provisión' : 'Editar Provisión'}</DialogTitle>
            <DialogDescription>Gasto a provisionar con fecha de vencimiento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="prov-nombre">Nombre</Label>
              <Input
                id="prov-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Pago Préstamo Banco"
                data-testid="input-prov-nombre"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="prov-vence">Vence</Label>
                <DatePickerInput
                  id="prov-vence"
                  value={form.vence}
                  onChange={(iso) => setForm({ ...form, vence: iso })}
                  data-testid="input-prov-vence"
                />
              </div>
              <div>
                <Label htmlFor="prov-monto">Monto (CLP)</Label>
                <Input
                  id="prov-monto"
                  type="number"
                  value={form.monto}
                  onChange={(e) => setForm({ ...form, monto: Number(e.target.value) || 0 })}
                  data-testid="input-prov-monto"
                />
              </div>
              <div>
                <Label htmlFor="prov-dia-mes" title="Día típico del mes (1-31). Ejemplo: IVA=20, Sueldos=5">Día mes</Label>
                <Input
                  id="prov-dia-mes"
                  type="number"
                  min={1}
                  max={31}
                  placeholder="—"
                  value={form.dia_mes ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : Math.min(Math.max(1, Number(e.target.value) || 1), 31);
                    setForm({ ...form, dia_mes: v });
                  }}
                  data-testid="input-prov-dia-mes"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">
              <strong>Día mes</strong>: día típico de pago. Ej: IVA=20, Sueldos=5, Imposiciones=10. Habilita "Generar próximos meses" desde la tabla.
            </p>
            <div className="flex items-center gap-2 pt-2">
              <Switch
                checked={form.activo}
                onCheckedChange={(v) => setForm({ ...form, activo: v })}
                data-testid="switch-prov-activo"
              />
              <Label>Activo (se suma al flujo)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-prov">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}



function GastosDiariosCard({ onSaved }) {
  const [cfg, setCfg] = useState({ monto: 500000, activo: true });
  const [original, setOriginal] = useState({ monto: 500000, activo: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await flujoCajaApi.getGastosDiariosConfig();
      const next = { monto: Number(res.data.monto) || 0, activo: !!res.data.activo };
      setCfg(next);
      setOriginal(next);
    } catch {
      // si falla, dejamos defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = cfg.monto !== original.monto || cfg.activo !== original.activo;

  const save = async () => {
    if (cfg.monto < 0) { toast({ title: 'Monto inválido', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await flujoCajaApi.updateGastosDiariosConfig({ monto: Number(cfg.monto), activo: !!cfg.activo });
      toast({ title: 'Gasto diario imprevistos actualizado', description: `${formatCLP(cfg.monto)} L-V · ${cfg.activo ? 'activo' : 'pausado'}` });
      setOriginal(cfg);
      onSaved?.();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error guardando config', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="mt-3 p-3 rounded-md border bg-violet-50 dark:bg-violet-950/20 border-violet-200 dark:border-violet-800" data-testid="gastos-diarios-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-start gap-2">
          <Wallet className="w-4 h-4 text-violet-700 dark:text-violet-300 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-violet-900 dark:text-violet-200">
              Gasto diario imprevistos · fila 20 del grid
            </p>
            <p className="text-[11px] text-violet-700 dark:text-violet-400">
              Monto fijo que se carga automáticamente cada día hábil L-V de la ventana 91d.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Switch
              checked={cfg.activo}
              onCheckedChange={(v) => setCfg((c) => ({ ...c, activo: !!v }))}
              data-testid="switch-gastos-diarios-activo"
            />
            <span className="text-[11px] text-violet-700 dark:text-violet-300">{cfg.activo ? 'Activo' : 'Pausado'}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-violet-700 dark:text-violet-300">$</span>
            <Input
              type="number"
              value={cfg.monto}
              onChange={(e) => setCfg((c) => ({ ...c, monto: Number(e.target.value) || 0 }))}
              className="h-8 w-32 text-sm tabular-nums"
              data-testid="input-gastos-diarios-monto"
            />
            <span className="text-[10px] text-violet-700 dark:text-violet-300">/ día L-V</span>
          </div>
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            className="h-8 bg-violet-600 hover:bg-violet-700 text-white text-xs"
            data-testid="btn-save-gastos-diarios"
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
